/** The languages this build ships. Adding one is a decision, not an inference */
export const languages = ['en', 'es'] as const;
export type Language = (typeof languages)[number];

/** A flat map of stable keys to text. A plural key holds its forms instead of a string */
export type Entries = Record<string, string | { one: string; other: string }>;

/**
 * The text of one language, with an explicit fallback and an explicit account of what is missing.
 *
 * Two rules come from the interface contract and are enforced here rather than trusted to reviewers.
 * A raw key is never rendered: a key the catalog does not hold falls back to English, and if English
 * does not hold it either the reader gets an em dash rather than `invitations.role.developer`. And what
 * fell back is *recorded*, so a gap is reportable instead of invisible until someone notices a screen
 * in the wrong language.
 *
 * Only interface text lives here. Identifiers, diagnostic codes and anything a person typed are never
 * translated; their surrounding explanation is.
 */
export class Catalog {
	#language: Language;
	get language() {
		return this.#language;
	}

	#entries: Entries;
	#fallback: Entries;
	#missing = new Set<string>();

	/**
	 * Keys that were asked for and were not in this language. Read by the diagnostics view, so a gap in
	 * a translation is something the team can see.
	 */
	get missing(): string[] {
		return [...this.#missing].sort();
	}

	constructor(language: Language, entries: Entries, fallback: Entries) {
		this.#language = language;
		this.#entries = entries;
		this.#fallback = fallback;
	}

	/**
	 * The text of a key.
	 *
	 * @param values Placeholders, written `{name}` in the catalog. A `count` also selects the plural
	 * form, using the language's own rule rather than an English-shaped "n === 1".
	 */
	text(key: string, values: Record<string, string | number> = {}): string {
		const entry = this.#entries[key] ?? this.#record(key);
		if (entry === undefined) return '—';

		const template = typeof entry === 'string' ? entry : entry[this.#plural(Number(values.count ?? 0))];
		return template.replace(/\{(\w+)\}/g, (whole, name: string) => {
			const value = values[name];
			return value === undefined ? whole : String(value);
		});
	}

	/**
	 * @returns Whether this catalog holds the key itself, without falling back
	 */
	holds(key: string): boolean {
		return key in this.#entries;
	}

	#record(key: string): string | { one: string; other: string } | undefined {
		this.#missing.add(key);
		return this.#fallback[key];
	}

	/**
	 * English and Spanish share a plural rule: one, and everything else. This is a method rather than an
	 * inline comparison so a language whose rule differs has somewhere to go.
	 */
	#plural(count: number): 'one' | 'other' {
		return count === 1 ? 'one' : 'other';
	}
}
